/* eslint-disable no-param-reassign */
import { API_URL } from '@shared/config'
import { notyError } from '@shared/lib/notifications'
import { getSessionStorageValue } from '@shared/lib/storage'
import { sleep } from '@shared/lib/utils'
import { clearAuth, storeModel } from '@shared/store'

export const apiInstance = {
  controllers: new Map(),

  //

  get: async (url, data = null, options = {}) => {
    return apiInstance._runRequest({
      type: 'GET',
      url: apiInstance._getUrl(url),
      data,
      options,
    })
  },

  getWithAbort: async (url, data = null, options = {}, serviceObj = {}) => {
    return apiInstance._runRequest({
      type: 'GET',
      url: apiInstance._getUrl(url),
      data,
      options,
      serviceObj,
    })
  },

  //

  post: async (url, data = {}, options = {}) => {
    return apiInstance._runRequest({
      type: 'POST',
      url: apiInstance._getUrl(url),
      data,
      options,
    })
  },

  postWithAbort: async (url, data = {}, options = {}, serviceObj = {}) => {
    return apiInstance._runRequest({
      type: 'POST',
      url: apiInstance._getUrl(url),
      data,
      options,
      serviceObj,
    })
  },

  //

  put: async (url, data = {}, options = {}) => {
    return apiInstance._runRequest({
      type: 'PUT',
      url: apiInstance._getUrl(url),
      data,
      options,
    })
  },

  patch: async (url, data = {}, options = {}) => {
    return apiInstance._runRequest({
      type: 'PATCH',
      url: apiInstance._getUrl(url),
      data,
      options,
    })
  },

  delete: async (url, data = null, options = {}) => {
    return apiInstance._runRequest({
      type: 'DELETE',
      url: apiInstance._getUrl(url),
      data,
      options,
    })
  },

  //
  //
  //
  //
  //

  cancelRequest: (url) => {
    if (apiInstance.controllers.has(url)) {
      apiInstance.controllers.get(url).abort()
    }
  },

  //
  //
  //
  //
  //

  _getUrl: (url) => `${API_URL}${url}`,

  _runRequest: async ({ type, url, data, options, serviceObj }, tries = 0) => {
    const { headers, downloadFile, retries } = options

    let finalUrl = url
    let body = null

    //

    const controller = new AbortController()

    apiInstance.controllers.set(url, controller)

    if (serviceObj) {
      serviceObj.abortController = controller

      delete serviceObj.wasUsed
    }

    //

    const requestHeaders = { ...apiInstance._getDefaultHeaders(), ...headers }

    if (type === 'GET' && data) {
      finalUrl += apiInstance._buildOptionsPath(data)
    } else if (data) {
      if (typeof data === 'string') {
        requestHeaders['Content-Type'] = 'application/x-www-form-urlencoded'

        body = data
      } else if (data instanceof FormData) {
        body = data
      } else {
        requestHeaders['Content-Type'] = 'application/json'

        body = JSON.stringify(data)
      }
    }

    //

    const REQUEST_OPTIONS = {
      method: type,
      headers: requestHeaders,
      credentials: 'include',
      //
      body,
      //
      signal: controller.signal,
    }

    //

    let timeoutAbort = null

    try {
      setTimeout(() => {
        timeoutAbort = true

        controller.abort()
      }, options.timeout || 100000)

      const response = await fetch(finalUrl, REQUEST_OPTIONS)

      //

      let responseData = null

      if (downloadFile) {
        const blob = await response.blob()

        const fileName = response.headers
          .get('Content-Disposition')
          ?.split('=')[1]

        responseData = { blob, fileName }
      } else if (
        response.headers.get('Content-Type')?.includes('application/json')
      ) {
        responseData = await response.json()
      } else {
        responseData = await response.text()
      }

      //

      const { status } = response
      const dataToReturn = responseData?.data || responseData || null

      if (!response.ok) {
        const error = new Error(responseData?.error || 'Request failed')

        error.status = status
        error.data = dataToReturn

        throw error
      }

      return {
        success: true,
        //
        status,
        //
        data: dataToReturn,
      }
    } catch (error) {
      if ([401, 407].includes(error.status) && !options.notThrowUnauthorized) {
        if (error.status === 401) {
          notyError('Unauthorized. Try to re-open the app.')
        } else if (error.status === 407) {
          notyError('Authentication timeout. Please re-open the app.')
        }

        // Handle unauthorized error globally
        storeModel.dispatch(clearAuth())

        return {
          success: false,
          //
          error,
          status: error.status,
        }
      }

      if (error.name === 'AbortError' && !timeoutAbort) {
        const returnError = new Error(
          serviceObj?.wasUsed ? 'Aborted' : 'Request timeout. Please try again'
        )

        returnError.status = 499

        return {
          success: false,
          //
          error: returnError,
          status: returnError.status,
        }
      }

      //

      if (
        retries &&
        (timeoutAbort ||
          error.status === 502 ||
          error.message === 'Failed to fetch') &&
        tries < retries
      ) {
        // await sleep(1000 * 2 ** tries) // Exponential backoff
        await sleep(1500) // simple delay

        //

        return apiInstance._runRequest(
          { type, url, data, options, serviceObj },
          tries + 1
        )
      }

      return {
        success: false,
        //
        error,
        status: error.status || -1,
      }
    } finally {
      if (serviceObj) {
        serviceObj.abortController = null
      }

      apiInstance.controllers.delete(url)
    }
  },

  _buildOptionsPath: (data, withStart = true) => {
    if (!data || !Object.keys(data).length) {
      return ''
    }

    return `${withStart ? '?' : ''}${new URLSearchParams(data).toString()}`
  },

  _getDefaultHeaders: () => ({
    Accept: 'application/json',
    Authorization: `tma ${getSessionStorageValue('initData', false) || ''}`,
    'Accept-Language':
      getSessionStorageValue('language', false) ||
      navigator.language.split('-')[0] ||
      'enUS',
  }),
}
